import { z } from 'zod';

import { getNumberOfNightsBetween } from '@/common/utils/date';
import {
  getTotalAdultGuestCount,
  getTotalChildGuestCount,
  getTotalGuestCount,
  transformRoomOccupancyListToUrlQuery,
} from '@/hotel/helpers/occupancy-type.helpers';
import { Occupancy } from '@/hotel/models/occupancy.model';

import { availabilityQueryToURLParamString } from '../helpers/query-url';

export type IAvailabilitySearchQueryInstance = z.infer<
  typeof AvailabilitySearchQuery
>;
export type IAvailabilitySearchQuerySnapshot = z.input<
  typeof AvailabilitySearchQuery
>;

export const availabilitySearchQuerySelectors = (
  query: IAvailabilitySearchQueryInstance
) => {
  function asURLQuery() {
    return {
      checkin: query.from,
      checkout: query.to,
      hotelCode: query.hotelCode,
      rateCode: query.rateCode,
      rateCodeType: query.rateCodeType,
      dayUse: query.dayUse ? 'true' : 'false',
      filter: {
        roomCode: query.filter?.roomCode,
        rateCode: query.filter?.rateCode,
      },
      ...transformRoomOccupancyListToUrlQuery(query.rooms),
    };
  }
  function asURLQueryString() {
    return availabilityQueryToURLParamString(asURLQuery());
  }

  return {
    asURLQuery,
    asURLQueryString,
    totalNightCount(): number {
      return getNumberOfNightsBetween(query.from, query.to);
    },
    totalGuestCount(): number {
      return getTotalGuestCount(query.rooms);
    },
    totalAdultCount(): number {
      return getTotalAdultGuestCount(query.rooms);
    },
    totalChildCount(): number {
      return getTotalChildGuestCount(query.rooms);
    },
    roomCount(): number {
      return query.rooms.length;
    },

    resultsURL() {
      return `/availability?${asURLQueryString()}`;
    },
  };
};

export const AvailabilitySearchQuery = z.object({
  from: z.string(),
  to: z.string(),
  hotelCode: z.string(),
  rateCode: z.string().optional(),
  rateCodeType: z.literal('BLOCK').optional(),
  rooms: z.array(Occupancy).default([]),
  dayUse: z.boolean(),
  filter: z
    .object({
      roomCode: z.array(z.string()).optional(),
      rateCode: z.array(z.string()).optional(),
    })
    .optional(),
});
